/**
 * Reading-time estimation from markdown or plain text.
 *
 * What: Counts words in content (optionally stripping frontmatter) and estimates reading time from a configurable words-per-minute rate.
 * Why: Provides a single, testable place for blog and other content to show accurate "X min read" values.
 * When: Update when changing the WPM constant, frontmatter stripping rules, or word-count logic.
 */

/** Typical adult reading speed in words per minute (conservative estimate). */
export const DEFAULT_WORDS_PER_MINUTE = 200;

/**
 * Strip YAML frontmatter from markdown if present.
 * Handles --- on its own line at the start; leaves the rest of the string unchanged.
 *
 * @param {string} raw - Raw markdown (may include optional frontmatter).
 * @returns {string} Body text without the leading frontmatter block.
 */
export function stripFrontmatter(raw: string): string {
  if (typeof raw !== 'string') {
    return '';
  }

  const trimmed = raw.trim();
  const open = '---';

  if (!trimmed.startsWith(open)) {
    return raw;
  }

  const afterOpen = trimmed.slice(open.length);
  const closeIndex = afterOpen.indexOf('\n---');

  if (closeIndex === -1) {
    return raw;
  }

  const afterBlock = afterOpen.slice(closeIndex + 4).trimStart();
  return afterBlock;
}

/**
 * Count words in a string (whitespace-separated tokens, excluding empty).
 *
 * @param {string} text - Plain text or markdown fragment.
 * @returns {number} Word count.
 */
export function getWordCount(text: string): number {
  if (typeof text !== 'string') {
    return 0;
  }

  const normalized = text.trim().replace(/\s+/g, ' ');
  if (!normalized) {
    return 0;
  }

  return normalized.split(' ').filter(Boolean).length;
}

/**
 * Get word count from full markdown, ignoring optional YAML frontmatter.
 *
 * @param {string} rawMarkdown - Full markdown (e.g. file contents).
 * @returns {number} Word count of the body only.
 */
export function getWordCountFromMarkdown(rawMarkdown: string): number {
  const body = stripFrontmatter(rawMarkdown);
  return getWordCount(body);
}

/**
 * Estimate reading time in whole minutes from word count.
 *
 * @param {number} wordCount - Total words.
 * @param {number} [wordsPerMinute=DEFAULT_WORDS_PER_MINUTE] - Reading speed (WPM).
 * @returns {number} Rounded minutes (minimum 1 when wordCount > 0).
 */
export function estimateReadingMinutes(
  wordCount: number,
  wordsPerMinute: number = DEFAULT_WORDS_PER_MINUTE
): number {
  const wpm = Number(wordsPerMinute);
  const safeWpm =
    Number.isFinite(wpm) && wpm > 0 ? wpm : DEFAULT_WORDS_PER_MINUTE;
  const count = Number(wordCount);
  if (!Number.isFinite(count) || count <= 0) {
    return 0;
  }
  const minutes = count / safeWpm;
  return Math.max(1, Math.round(minutes));
}

/**
 * Compute reading time in minutes from raw markdown (body only, no frontmatter).
 *
 * @param {string} rawMarkdown - Full markdown content.
 * @param {number} [wordsPerMinute=DEFAULT_WORDS_PER_MINUTE] - Reading speed (WPM).
 * @returns {number} Estimated minutes (minimum 1 when content has words).
 */
export function getReadingTimeMinutesFromMarkdown(
  rawMarkdown: string,
  wordsPerMinute: number = DEFAULT_WORDS_PER_MINUTE
): number {
  const wordCount = getWordCountFromMarkdown(rawMarkdown);
  return estimateReadingMinutes(wordCount, wordsPerMinute);
}
